import { memo, useMemo, type FC } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Loader } from '../../../../Elements/Loader/index.tsx';
import { dataTableSection } from '../../constants.ts';
import { useTableContext } from '../../context.tsx';
import type { PageDetails } from '../../types.ts';
import { PageInfo } from '../PageInfo/index.tsx';
import { PaginationControls } from '../PaginationControls/index.tsx';
import { BulkActionsLabel, BulkActionsWrapper, FiltersWrapper, InfoInner, PageInfoWrapper, InfoWrapper } from './styled.ts';
import type { InfoProps } from './types.ts';

export const Info: FC<InfoProps> = memo(function Info({
  className,
  info,
  total,
  pagination,
  isLoading,
  filters,
  bulkActions,
  bulkActionsLabel,
  showBulkActions,
  filtersLoading,
  sorting,
  dataTestId,
}) {
  const { compact } = useTableContext();
  const pageInfo = useMemo<PageDetails>(() => info ?? { from: 1, to: total, total }, [info, total]);
  const increasedVerticalPadding = Boolean((filters || bulkActions) && total !== undefined);
  const topBorder = Boolean(filters || showBulkActions);
  const withSorting = Boolean(sorting);
  const pageInfoMobileMlAuto = total !== undefined || (pageInfo && pageInfo.to === pageInfo.total);

  const infoFilters = filters && <FiltersWrapper $compact={compact}>{!filtersLoading ? filters : <Loader />}</FiltersWrapper>;
  const infoActions = (bulkActionsLabel || bulkActions) && (
    <BulkActionsWrapper $compact={compact}>
      {bulkActionsLabel && <BulkActionsLabel>{bulkActionsLabel}</BulkActionsLabel>}
      {bulkActions}
    </BulkActionsWrapper>
  );

  const nextPage = useCallbackRef(() => {
    pagination?.setPage((prev) => prev + 1);
  });

  const prevPage = useCallbackRef(() => {
    pagination?.setPage((prev) => prev - 1);
  });

  return (
    <InfoWrapper className={className} {...dataTableSection} data-testid={dataTestId}>
      {showBulkActions ? infoActions : infoFilters}
      <InfoInner $increasedVerticalPadding={increasedVerticalPadding} $topBorder={topBorder} $withSorting={withSorting} $compact={compact}>
        {sorting}
        <PageInfoWrapper $mobileMlAuto={pageInfoMobileMlAuto}>
          <PageInfo dataTestId='table-total-info' {...pageInfo} isLoading={isLoading} />
        </PageInfoWrapper>
        {pagination && (pageInfo.total > pagination.perPage || isLoading) && (
          <PaginationControls
            page={pagination.page}
            perPage={pagination.perPage}
            total={pageInfo.total}
            onPrev={prevPage}
            onNext={nextPage}
          />
        )}
      </InfoInner>
    </InfoWrapper>
  );
});
